(() => {
  if (customElements.get('polardress-custom-size')) return;

  const formatNumber = (value) => {
    const rounded = Math.round(value * 10) / 10;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  };

  class PolardressCustomSize extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.variantForm = this.closest('form.product__form-container');
      this.productSection = this.closest('[data-section-type="product__section"]');
      this.toggle = this.querySelector('[data-polardress-unit-toggle]');
      this.decorateSizeSelector();

      this.onVariantChange = this.onVariantChange.bind(this);
      this.onUnitToggle = this.onUnitToggle.bind(this);

      if (this.variantForm) this.variantForm.addEventListener('change', this.onVariantChange);
      if (this.toggle) this.toggle.addEventListener('click', this.onUnitToggle);
      if (this.variantForm) {
        this.sizeSelectorObserver = new MutationObserver(() => this.decorateSizeSelector());
        this.sizeSelectorObserver.observe(this.variantForm, { childList: true, subtree: true });
      }
      this.addEventListener('invalid', () => this.classList.add('has-validation-error'), true);

      this.populateMeasurementSelects(this.dataset.measurementUnit || 'INCH', false);
      this.syncPanel();
    }

    disconnectedCallback() {
      if (this.variantForm) this.variantForm.removeEventListener('change', this.onVariantChange);
      if (this.toggle) this.toggle.removeEventListener('click', this.onUnitToggle);
      this.sizeSelectorObserver?.disconnect();
    }

    decorateSizeSelector() {
      this.sizeContainer = this.variantForm
        ?.querySelector('input[name="size"]')
        ?.closest('.swatches__container');
      this.sizeContainer?.classList.add('polardress-size-selector');
      this.sizeContainer
        ?.querySelector('.polardress-custom-size-option')
        ?.closest('.js-swatch-element')
        ?.classList.add('polardress-custom-size-swatch');
    }

    selectedSizeValue() {
      if (!this.variantForm) return '';
      const controls = Array.from(
        this.variantForm.querySelectorAll('input[type="radio"]:checked, select.js-variant-selector')
      );
      const sizeControl = controls.find((control) => {
        const name = (control.getAttribute('name') || '').toLowerCase();
        return !name.startsWith('properties[') && name.includes('size');
      });
      return sizeControl ? sizeControl.value : '';
    }

    onVariantChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
      const name = (target.getAttribute('name') || '').toLowerCase();
      if (name.startsWith('properties[') || !name.includes('size')) return;

      const isActive = this.syncPanel();
      if (isActive) {
        window.requestAnimationFrame(() => {
          this.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          this.querySelector('[data-polardress-required]')?.focus({ preventScroll: true });
        });
      }
    }

    syncPanel() {
      const isActive = this.selectedSizeValue() === this.dataset.customSizeValue;
      this.hidden = !isActive;
      this.setAttribute('aria-hidden', String(!isActive));
      if (this.variantForm) {
        this.variantForm.classList.toggle('is-polardress-custom-size', isActive);
      }
      this.querySelectorAll('[data-polardress-custom-field]').forEach((field) => {
        field.disabled = !isActive;
        field.required = isActive && field.hasAttribute('data-polardress-required');
        if (!isActive) field.setCustomValidity('');
      });
      if (!isActive) this.classList.remove('has-validation-error');
      return isActive;
    }

    onUnitToggle() {
      const nextUnit = this.dataset.measurementUnit === 'CM' ? 'INCH' : 'CM';
      this.populateMeasurementSelects(nextUnit, true);
    }

    populateMeasurementSelects(unit, preserveSelection) {
      const previousUnit = this.dataset.measurementUnit || 'INCH';

      this.querySelectorAll('[data-polardress-measurement-select]').forEach((select) => {
        const previousValue = preserveSelection ? Number.parseFloat(select.value) : Number.NaN;
        const convertedPrevious = Number.isFinite(previousValue) && previousUnit !== unit
          ? previousValue * (unit === 'CM' ? 2.54 : 1 / 2.54)
          : previousValue;
        const minimumInches = Number.parseFloat(select.dataset.minIn);
        const maximumInches = Number.parseFloat(select.dataset.maxIn);
        const stepInches = Number.parseFloat(select.dataset.stepIn);
        const minimum = unit === 'CM' ? Math.round(minimumInches * 2.54) : minimumInches;
        const maximum = unit === 'CM' ? Math.round(maximumInches * 2.54) : maximumInches;
        const step = unit === 'CM' ? 1 : stepInches;
        const suffix = unit === 'CM' ? 'cm' : 'in';
        const optionCount = Math.floor((maximum - minimum) / step) + 1;
        const fragment = document.createDocumentFragment();
        let nearestOption = null;
        let nearestDistance = Number.POSITIVE_INFINITY;

        fragment.append(new Option('Please Select', ''));
        for (let index = 0; index < optionCount; index += 1) {
          const numericValue = Math.round((minimum + index * step) * 10) / 10;
          const displayValue = formatNumber(numericValue);
          const option = new Option(`${displayValue} ${suffix}`, `${displayValue} ${suffix}`);
          if (Number.isFinite(convertedPrevious)) {
            const distance = Math.abs(numericValue - convertedPrevious);
            if (distance < nearestDistance) {
              nearestDistance = distance;
              nearestOption = option;
            }
          }
          fragment.append(option);
        }
        select.replaceChildren(fragment);
        if (nearestOption) nearestOption.selected = true;
      });

      this.dataset.measurementUnit = unit;
      const unitInput = this.querySelector('[data-polardress-unit-value]');
      if (unitInput) unitInput.value = unit;
      if (this.toggle) this.toggle.setAttribute('aria-checked', String(unit === 'CM'));
      this.querySelectorAll('[data-polardress-unit-label]').forEach((label) => {
        label.classList.toggle('is-active', label.dataset.polardressUnitLabel === unit);
      });
    }
  }

  customElements.define('polardress-custom-size', PolardressCustomSize);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.id) return;
    const panel = Array.from(document.querySelectorAll('polardress-custom-size')).find(
      (candidate) => candidate.dataset.buttonsFormId === form.id && !candidate.hidden
    );
    if (!panel) return;

    const invalidField = Array.from(panel.querySelectorAll('[data-polardress-required]')).find(
      (field) => !field.disabled && !field.checkValidity()
    );
    if (!invalidField) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    panel.classList.add('has-validation-error');
    panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
    invalidField.reportValidity();
    invalidField.focus({ preventScroll: true });
  }, true);
})();
